import { useState, useEffect, useCallback } from 'react';
import CompensationUnlockService from '../services/CompensationUnlockService';
import { useUserRole } from '../../../hooks/useUserRole';

export function useCompensationUnlock() {
  const { isAdmin } = useUserRole();
  const [isUnlocked, setIsUnlocked] = useState(CompensationUnlockService.isUnlocked());
  const [remainingSec, setRemainingSec] = useState(CompensationUnlockService.getTimeRemaining());
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinError, setPinError] = useState<string>('');
  const [unlockPromiseResolve, setUnlockPromiseResolve] = useState<((value: boolean) => void) | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (CompensationUnlockService.isUnlocked()) {
        const remaining = CompensationUnlockService.getTimeRemaining();
        setRemainingSec(remaining);
        setIsUnlocked(true);

        if (remaining <= 0) {
          CompensationUnlockService.clearTicket();
          setIsUnlocked(false);
        }
      } else {
        setIsUnlocked(false);
        setRemainingSec(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handlePinSubmit = useCallback(async (pin: string) => {
    try {
      setPinError('');
      const response = await CompensationUnlockService.unlock(pin);
      setShowPinDialog(false);
      setIsUnlocked(true);
      setRemainingSec(response.ttl);
      if (unlockPromiseResolve) {
        unlockPromiseResolve(true);
        setUnlockPromiseResolve(null);
      }
    } catch (e: any) {
      const errorMsg = e?.response?.data?.message || e?.response?.data?.detail || 'Invalid PIN';
      setPinError(errorMsg);
      throw e;
    }
  }, [unlockPromiseResolve]);

  const ensureUnlocked = useCallback((): Promise<boolean> => {
    const isDevelopment = import.meta.env.DEV;
    const pinDisabled = import.meta.env.VITE_DISABLE_COMP_PIN === 'true';

    if (isDevelopment && pinDisabled) {
      return Promise.resolve(true);
    }

    if (isAdmin) {
      return Promise.resolve(true);
    }

    if (CompensationUnlockService.isUnlocked()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      setUnlockPromiseResolve(() => resolve);
      setShowPinDialog(true);
    });
  }, [isAdmin]);

  const cancelUnlock = useCallback(() => {
    setShowPinDialog(false);
    setPinError('');
    if (unlockPromiseResolve) {
      unlockPromiseResolve(false);
      setUnlockPromiseResolve(null);
    }
  }, [unlockPromiseResolve]);

  return {
    isUnlocked,
    remainingSec,
    ensureUnlocked,
    showPinDialog,
    pinError,
    handlePinSubmit,
    cancelUnlock,
  };
}
